import { useEffect, useMemo, useState } from 'react'

import { formatCost, getPriceCalculatorForQuantity, formatNumbers } from 'utils'

import { QuantitiesPerSize } from '@/components/order'
import { useCartItemStyle } from '@/context/CartItemStyleContext'
import { ModalRemoveFromCart } from '@/components/modals'
import { useCartContext } from '@/context/CartContext'
import { getCartItemsPrice, getItemStyleNewPrice } from '@/lib/utils/prices'
import { useBuilder } from '@/context/BuilderContext'
import { getStyleBySlug } from '@/lib/graphql/realthread/queries'
import CanvasReview from '@/components/CanvasReview'
import { useCartItem } from '@/context/CartItemContext'
import clsx from 'clsx'

type Props = {
	readOnly?: boolean
}

export default function CartItemStyleQuantity({ readOnly = false }: Props) {
	const { cartItemsQuantities, setIsAnyQuantityError } = useCartContext()
	const { cartItem, cartItemQuantity, priceCalculatorsForRecordType, recordType } = useCartItem()
	const { setSelectedColor, selectedColor, setIsTotalQuantityInputActive, defaultCartItemStyle } =
		useBuilder()
	const [styleName, setStyleName] = useState('')

	const {
		style: itemStyle,
		itemStyleTotalQuantity,
		setItemStyleTotalQuantity,
		quantityRef,
		isQuantityError,
		locations
	} = useCartItemStyle()

	useEffect(() => {
		const getStyleData = async () => {
			const data = await getStyleBySlug(itemStyle.style.slug)

			setSelectedColor(data.data.style.colors.find((color) => color.sfid === itemStyle.color.sfid))
			setStyleName(data.data.style.detail)
		}

		getStyleData()
	}, [itemStyle.style.slug, itemStyle.color.sfid, setSelectedColor])

	useEffect(() => {
		setIsTotalQuantityInputActive(false)
		setIsAnyQuantityError(isQuantityError)
	}, [isQuantityError, setIsAnyQuantityError, setIsTotalQuantityInputActive])

	useEffect(() => {
		setItemStyleTotalQuantity(itemStyle.quantity)
	}, [itemStyle.quantity, setItemStyleTotalQuantity])

	const [showRemoveItemModal, setShowRemoveItemModal] = useState(false)

	const price = useMemo(() => {
		if (!priceCalculatorsForRecordType) return null

		const currentQuantity = !defaultCartItemStyle
			? cartItemQuantity > 0
				? cartItemQuantity + itemStyleTotalQuantity
				: itemStyleTotalQuantity
			: cartItemQuantity > 0 && cartItemQuantity

		const priceCalculator = getPriceCalculatorForQuantity(
			currentQuantity,
			priceCalculatorsForRecordType,
			recordType
		)

		return getItemStyleNewPrice(currentQuantity, selectedColor?.price, locations, priceCalculator)
	}, [
		cartItemQuantity,
		itemStyleTotalQuantity,
		defaultCartItemStyle,
		recordType,
		selectedColor?.price,
		locations,
		priceCalculatorsForRecordType
	])

	const itemStyleTotalPrice = useMemo(() => {
		return getCartItemsPrice(cartItemsQuantities, itemStyle, price)
	}, [cartItemsQuantities, price, itemStyle])

	const surchargesPerProduct = useMemo(() => {
		const surcharges = itemStyle.products.map((size) => {
			const priceCalculatorForQuantity = getPriceCalculatorForQuantity(
				cartItemQuantity,
				priceCalculatorsForRecordType,
				recordType
			)

			const surchargePrice = getItemStyleNewPrice(
				cartItemQuantity,
				size.unitCost,
				locations,
				priceCalculatorForQuantity
			)

			if (surchargePrice === price) return {}

			return {
				[size.sizeId]: formatCost(surchargePrice)
			}
		})

		// filter out empty objects
		return surcharges.filter((size) => Object.keys(size).length > 0)
	}, [
		cartItemQuantity,
		itemStyle?.products,
		price,
		locations,
		priceCalculatorsForRecordType,
		recordType
	])

	return (
		<div ref={quantityRef}>
			<li>
				<h3 className="card-head print:mb-0 print:border-b print:border-g-200">
					{itemStyle?.style.fullName + ' ' + styleName}
				</h3>

				<ol>
					<li className="p-5 pb-4 border-b border-g-200 last:border-0">
						<div className="flex flex-grow">
							<div className="items-center mb-2 md:mb-0 w-full">
								<div className="hidden md:flex mb-1 justify-between items-center">
									<h4 className="flex items-center text-sm flex-wrap">
										<span>{itemStyle.color.name}</span>
										<span className="bullet"></span>
										<span>
											{formatNumbers(itemStyleTotalQuantity)} @{' '}
											{isNaN(price) ? '' : formatCost(price)} each
										</span>
										{itemStyle.products.length > 1 && surchargesPerProduct.length > 0 && (
											<>
												{surchargesPerProduct.map((sizeId, index) => {
													return (
														<div key={index} className="ml-1.5">
															{Object.keys(sizeId).map((size) => {
																return (
																	<span
																		key={sizeId[size]}
																		className={clsx('text-g-500', index !== 0 && 'ml-1')}
																	>
																		{
																			// add open parenthesis before first size
																			index === 0 ? '(' : ''
																		}
																		{size} {sizeId[size]}
																		{
																			//add comma between sizes and close parenthesis after last size
																			surchargesPerProduct.length > 1 &&
																			surchargesPerProduct.length - 1 !== index
																				? ', '
																				: ')'
																		}
																	</span>
																)
															})}
														</div>
													)
												})}
											</>
										)}
										{!readOnly && (
											<>
												<span className="bullet"></span>
												<button
													className="hyperlink"
													type="button"
													onClick={() => setShowRemoveItemModal(true)}
												>
													Remove
												</button>
											</>
										)}
									</h4>

									{!isNaN(itemStyleTotalPrice) && (
										<div className="hidden md:block flex-grow text-right justify-end">
											<span className="block font-medium">{formatCost(itemStyleTotalPrice)}</span>
										</div>
									)}
								</div>
								<div className="flex items-start">
									<CanvasReview cartItem={cartItem} selectedColor={selectedColor} />

									<div className="flex-grow mb-4 md:mb-0">
										<QuantitiesPerSize shouldAutoSave readOnly={readOnly} />
									</div>
								</div>
							</div>
						</div>
					</li>
				</ol>

				<ModalRemoveFromCart
					show={showRemoveItemModal}
					setShow={setShowRemoveItemModal}
					itemStyle={itemStyle}
				/>
			</li>
		</div>
	)
}
